import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useDemo } from '../../contexts/DemoContext';
import { getGoalById, addSignature } from '../../services/goalsService';
import { isDemoGoalId, getDemoGoalById } from '../../services/demoData';
import { generateGoalsDocx } from '../../services/docxService';
import { GoalDocument } from '../../types';
import {
  ArrowLeft,
  Download,
  CheckCircle2,
  Clock,
  XCircle,
  Pen,
  Edit,
  FlaskConical
} from 'lucide-react';
import { format } from 'date-fns';

const statusColors: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  submitted: 'bg-yellow-100 text-yellow-700',
  initial_review: 'bg-blue-100 text-blue-700',
  approved: 'bg-green-100 text-green-700',
  final_review: 'bg-purple-100 text-purple-700',
  completed: 'bg-emerald-100 text-emerald-700',
};

const statusLabels: Record<string, string> = {
  draft: 'Draft',
  submitted: 'Awaiting Review',
  initial_review: 'Under Review',
  approved: 'Approved',
  final_review: 'Final Review',
  completed: 'Completed',
};

const ViewGoal = () => {
  const { goalId } = useParams<{ goalId: string }>();
  const { user, firebaseUser, isSuperAdmin } = useAuth();
  useDemo(); // Hook needed for context but demo detection uses isDemoGoalId
  const navigate = useNavigate();
  const [goal, setGoal] = useState<GoalDocument | null>(null);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);

  // Check if this is a demo goal
  const isDemo = goalId ? isDemoGoalId(goalId) : false;

  useEffect(() => {
    const fetchGoal = async () => {
      if (!goalId) return;
      try {
        if (isDemo) {
          // Demo mode - fetch from in-memory state
          const demoGoal = getDemoGoalById(goalId);
          setGoal(demoGoal);
        } else {
          const goalDoc = await getGoalById(goalId);
          setGoal(goalDoc);
        }
      } catch (error) {
        console.error('Error fetching goal:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchGoal();
  }, [goalId, isDemo]);

  const handleDownload = async () => {
    if (!goal) return;
    try {
      await generateGoalsDocx(goal);
    } catch (error) {
      console.error('Error generating DOCX:', error);
    }
  };

  const handleSign = async (type: 'employeeSet' | 'managerSet' | 'employeeReview' | 'managerReview') => {
    if (!goal || !goalId || !user || isDemo) return;

    setSigning(true);
    try {
      await addSignature(goalId, type, user.displayName);
      const updated = await getGoalById(goalId);
      setGoal(updated);
    } catch (error) {
      console.error('Error adding signature:', error);
    } finally {
      setSigning(false);
    }
  };

  const formatDate = (timestamp: any): string => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return format(date, 'MMM d, yyyy');
  };

  const isOwner = isDemo || firebaseUser?.uid === goal?.userId;
  const isManager = isDemo || user?.email === goal?.managerEmail;

  // Check if user can edit
  const canEdit = () => {
    if (!goal) return false;
    if (isDemo && goal.status !== 'completed') return true;
    if (isSuperAdmin && goal.status !== 'completed') return true;
    if (isOwner && ['draft', 'submitted'].includes(goal.status)) return true;
    if (isManager && ['submitted', 'initial_review'].includes(goal.status)) return true;
    return false;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!goal) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Goal not found</p>
        <button
          onClick={() => navigate('/dashboard')}
          className="mt-4 text-blue-600 hover:text-blue-700"
        >
          Go back
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Demo indicator */}
      {isDemo && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2 text-amber-800">
          <FlaskConical className="w-5 h-5" />
          <span className="font-medium">Demo Goal</span>
          <span className="text-sm">- This is sample data for training purposes</span>
        </div>
      )}

      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Q{goal.quarter} {goal.year} Goals</h1>
            <p className="text-gray-500">{goal.userName} • {goal.userTitle}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {isDemo && (
            <span className="px-2 py-1 rounded text-xs bg-amber-100 text-amber-700">
              <FlaskConical className="w-3 h-3 inline mr-1" />
              Demo
            </span>
          )}
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusColors[goal.status]}`}>
            {statusLabels[goal.status]}
          </span>
          {canEdit() && (
            <button
              onClick={() => navigate(`/goals/${goal.id}/edit`)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200 transition-colors"
            >
              <Edit className="w-4 h-4" />
              Edit
            </button>
          )}
          <button
            onClick={handleDownload}
            className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <Download className="w-4 h-4" />
            Download DOCX
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-6">
        <div className="p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Goals</h2>
        </div>
        <div className="divide-y">
          {goal.goals.map((g, index) => (
            <div key={index} className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-gray-900">
                      {index + 1}. {g.title}
                    </span>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      g.type === 'normal' ? 'bg-blue-100 text-blue-700' :
                      g.type === 'stretch' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {g.type}
                    </span>
                  </div>
                  <p className="text-gray-600 mt-1">{g.description}</p>
                </div>

                <div className="flex items-center gap-4 ml-4">
                  {g.initialApproval !== null && (
                    <div className="text-center">
                      <p className="text-xs text-gray-500 mb-1">Approved</p>
                      {g.initialApproval ? (
                        <CheckCircle2 className="w-5 h-5 text-green-500 mx-auto" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-500 mx-auto" />
                      )}
                    </div>
                  )}

                  {g.employeeRating && (
                    <div className="text-center">
                      <p className="text-xs text-gray-500 mb-1">Employee</p>
                      <span className={`px-2 py-1 rounded text-sm font-medium ${
                        g.employeeRating === 'A' ? 'bg-green-100 text-green-700' :
                        g.employeeRating === 'B' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {g.employeeRating}
                      </span>
                    </div>
                  )}
                  {g.managerRating && (
                    <div className="text-center">
                      <p className="text-xs text-gray-500 mb-1">Manager</p>
                      <span className={`px-2 py-1 rounded text-sm font-medium ${
                        g.managerRating === 'A' ? 'bg-green-100 text-green-700' :
                        g.managerRating === 'B' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {g.managerRating}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {(goal.salesGoal.percentage || goal.performanceGoal.percentage) && (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-6">
          <div className="p-4 border-b">
            <h2 className="text-lg font-semibold text-gray-900">Incentive Calculation</h2>
            <p className="text-sm text-gray-500">Manager only section</p>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">Sales Goal</p>
                <p className="text-lg font-semibold">
                  {goal.salesGoal.percentage}% reached
                </p>
                <p className="text-sm text-gray-600">
                  ${goal.salesGoal.amount?.toLocaleString() || 0}
                </p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">Performance Goal</p>
                <p className="text-lg font-semibold">
                  {goal.performanceGoal.percentage}% reached
                </p>
                <p className="text-sm text-gray-600">
                  ${goal.performanceGoal.amount?.toLocaleString() || 0}
                </p>
              </div>
            </div>
            <div className="mt-4 p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-600">Total Quarterly Incentive</p>
              <p className="text-2xl font-bold text-blue-700">
                ${((goal.salesGoal.amount || 0) + (goal.performanceGoal.amount || 0)).toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      )}

      {Object.values(goal.behavioralRatings).some(r => r !== null) && (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-6">
          <div className="p-4 border-b">
            <h2 className="text-lg font-semibold text-gray-900">Behavioral Performance</h2>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {([
                { key: 'respectForOthers', label: 'Respect for Others' },
                { key: 'integrity', label: 'Integrity' },
                { key: 'teamwork', label: 'Teamwork & Collaboration' },
                { key: 'biasForAction', label: 'Bias for Action' },
                { key: 'carefulCommunication', label: 'Careful Communication' },
                { key: 'growthMindset', label: 'Growth Mindset' },
                { key: 'leadByExample', label: 'Lead by Example' },
              ] as const).map(item => {
                const rating = goal.behavioralRatings[item.key];
                if (!rating) return null;
                return (
                  <div key={item.key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-600">{item.label}</span>
                    <span className={`px-2 py-1 rounded text-sm font-medium ${
                      rating === 'A' ? 'bg-green-100 text-green-700' :
                      rating === 'B' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {rating}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Signatures</h2>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3">Goals Set</h3>
              <div className="space-y-3">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Employee</span>
                    {goal.signatures.employeeSet ? (
                      <div className="text-right">
                        <p className="font-signature text-xl text-gray-900">
                          {goal.signatures.employeeSet.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatDate(goal.signatures.employeeSet.date)}
                        </p>
                      </div>
                    ) : (
                      <Clock className="w-4 h-4 text-gray-400" />
                    )}
                  </div>
                </div>

                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Manager</span>
                    {goal.signatures.managerSet ? (
                      <div className="text-right">
                        <p className="font-signature text-xl text-gray-900">
                          {goal.signatures.managerSet.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatDate(goal.signatures.managerSet.date)}
                        </p>
                      </div>
                    ) : (
                      <Clock className="w-4 h-4 text-gray-400" />
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3">Goals Reviewed</h3>
              <div className="space-y-3">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Employee</span>
                    {goal.signatures.employeeReview ? (
                      <div className="text-right">
                        <p className="font-signature text-xl text-gray-900">
                          {goal.signatures.employeeReview.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatDate(goal.signatures.employeeReview.date)}
                        </p>
                      </div>
                    ) : isOwner && goal.status === 'completed' && !isDemo ? (
                      <button
                        onClick={() => handleSign('employeeReview')}
                        disabled={signing}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50"
                      >
                        <Pen className="w-3 h-3" />
                        Sign
                      </button>
                    ) : (
                      <Clock className="w-4 h-4 text-gray-400" />
                    )}
                  </div>
                </div>

                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Manager</span>
                    {goal.signatures.managerReview ? (
                      <div className="text-right">
                        <p className="font-signature text-xl text-gray-900">
                          {goal.signatures.managerReview.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatDate(goal.signatures.managerReview.date)}
                        </p>
                      </div>
                    ) : isManager && goal.status === 'completed' && goal.signatures.employeeReview && !isDemo ? (
                      <button
                        onClick={() => handleSign('managerReview')}
                        disabled={signing}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50"
                      >
                        <Pen className="w-3 h-3" />
                        Sign
                      </button>
                    ) : (
                      <Clock className="w-4 h-4 text-gray-400" />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewGoal;